import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

import { Icon } from './Icon';

/**
 * One button language for the whole product. What each is for:
 *
 *   primary    the one main action on a screen or in a dialog
 *   secondary  supporting actions beside it (Cancel, Export, Filters)
 *   outline    a lighter primary -- an important action that is not *the* one
 *   ghost      tertiary actions and toolbar buttons
 *   success    a positive decision that is not routine (Approve, Verify)
 *   danger     a destructive or irreversible decision (Delete, Reject, Ban)
 *   ghost*     row actions: quiet until pointed at, then the colour of their
 *              consequence -- a dozen filled buttons in a column is all
 *              shout and no hierarchy.
 */
const VARIANTS = {
  // Filled buttons lift a pixel on hover with a soft coloured shadow -- the
  // one bit of motion that says "this is the thing to press".
  primary:
    'bg-brand-600 text-white shadow-sm shadow-brand-600/25 hover:bg-brand-700 ' +
    'hover:-translate-y-px hover:shadow-md hover:shadow-brand-600/30 focus-visible:outline-brand-600',
  secondary:
    'border-line bg-surface text-ink border shadow-xs hover:border-brand-300 hover:text-brand-700 hover:bg-brand-50/60',
  outline:
    'border-brand-300 bg-surface text-brand-700 border shadow-xs hover:border-brand-400 ' +
    'hover:bg-brand-50',
  ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink',
  success:
    'bg-success-600 text-white shadow-sm shadow-success-600/20 hover:bg-success-700 ' +
    'hover:-translate-y-px hover:shadow-md focus-visible:outline-success-600',
  danger:
    'bg-danger-600 text-white shadow-sm shadow-danger-600/20 hover:bg-danger-700 ' +
    'hover:-translate-y-px hover:shadow-md focus-visible:outline-danger-600',
  ghostPositive: 'text-ink-muted hover:bg-success-50 hover:text-success-700',
  ghostDanger: 'text-ink-muted hover:bg-danger-50 hover:text-danger-700',
  ghostBrand: 'text-ink-muted hover:bg-brand-50 hover:text-brand-700',

  // Tinted: a labelled action whose colour says what kind of act it is,
  // without the weight of a filled button. For record headers, where several
  // decisions sit side by side and each has to read at a glance.
  tintPositive:
    'border border-success-200 bg-success-50 text-success-700 hover:bg-success-100 ' +
    'focus-visible:outline-success-600',
  tintDanger:
    'border border-danger-200 bg-danger-50 text-danger-700 hover:bg-danger-100 ' +
    'focus-visible:outline-danger-600',
  tintWarning:
    'border border-warning-200 bg-warning-50 text-warning-800 hover:bg-warning-100 ' +
    'focus-visible:outline-warning-600',
  tintBrand:
    'border border-brand-200 bg-brand-50 text-brand-700 hover:border-brand-300 hover:bg-brand-100 ' +
    'focus-visible:outline-brand-600',

  // On the dark Wonder ground the hierarchy inverts: the filled button is
  // white and the quiet one is a hairline of the text colour. Neither touches
  // the brand ramp -- purple on purple has nowhere left to go.
  light: 'bg-white text-night hover:bg-white/90 focus-visible:outline-white',
  outlineLight: 'border-night-line text-on-night hover:border-on-night/60 border hover:bg-white/10',
};

const SIZES = {
  // Compact console controls: 28 / 32 / 36px. A minimum rather than a fixed
  // height, so a long label on a phone wraps inside the button instead of
  // running out of it.
  xs: 'min-h-7 px-2.5 py-1 text-xs',
  sm: 'min-h-8 px-3 py-1 text-sm',
  md: 'min-h-9 px-3.5 py-1.5 text-sm',
  lg: 'min-h-12 px-6 py-2 text-base',

  // The marketing pills run taller and wider than the app's controls, and
  // their own scale rather than a tweak of `md`/`lg` keeps the console from
  // inheriting hero proportions.
  pillSm: 'h-10 px-5 text-[0.9375rem]',
  pillMd: 'h-12 px-6 text-base',
};

const SHAPES = {
  default: 'rounded-input',
  pill: 'rounded-full',
};

// `max-w-full` + wrapping below `sm`: a button never pushes past its row on a
// phone; from `sm` up there is room, so labels stay on one line.
const BASE =
  'inline-flex max-w-full items-center justify-center gap-2 font-medium leading-tight text-center ' +
  'transition duration-150 ease-out whitespace-normal sm:whitespace-nowrap ' +
  'active:translate-y-0 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 ' +
  'disabled:hover:translate-y-0 disabled:active:scale-100';

export function Button({
  variant = 'primary',
  size = 'md',
  shape = 'default',
  isLoading = false,
  fullWidth = false,
  className,
  children,
  disabled,
  ...props
}) {
  return (
    <button
      className={cn(
        BASE,
        VARIANTS[variant],
        SIZES[size],
        SHAPES[shape],
        fullWidth && 'w-full',
        className,
      )}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading && <Spinner />}
      {children}
    </button>
  );
}

/** Same visual language as Button, but navigates. */
export function LinkButton({
  to,
  variant = 'primary',
  size = 'md',
  shape = 'default',
  className,
  children,
  ...props
}) {
  return (
    <Link
      to={to}
      className={cn(BASE, VARIANTS[variant], SIZES[size], SHAPES[shape], className)}
      {...props}
    >
      {children}
    </Link>
  );
}

/** Square, label-less. The label still exists -- as the tooltip and the
 * accessible name -- because an icon on its own is a guess. */
const ICON_SIZES = {
  sm: 'size-8 shrink-0',
  md: 'size-9 shrink-0',
};

/**
 * Row actions, icon-only and quiet, as in a working dashboard: a glyph on no
 * background until pointed at. The variant still carries the consequence --
 * a hairline tint of it at rest so approve and reject stay told apart, and a
 * soft wash of it on hover. The label lives in the tooltip and the accessible
 * name.
 */
const ICON_VARIANTS = {
  // Darker than the muted text colour: a bare glyph needs the contrast a
  // labelled button gets from its box.
  ghost: 'text-ink/75 hover:bg-brand-50 hover:text-brand-700',
  // A pressed toggle (a saved bookmark) keeps a wash, so on and off differ.
  ghostBrand:
    'text-brand-600 hover:bg-brand-50 hover:text-brand-700 aria-pressed:bg-brand-50 aria-pressed:ring-1 aria-pressed:ring-brand-200 aria-pressed:ring-inset',
  ghostPositive: 'text-success-600 hover:bg-success-50 hover:text-success-700',
  ghostDanger: 'text-ink/75 hover:bg-danger-50 hover:text-danger-600',
};

/** Icon buttons use the tinted tiles above; any other variant is shared. */
function iconVariant(variant) {
  return ICON_VARIANTS[variant] ?? VARIANTS[variant];
}

/** Room a label needs above its button before it flips below instead. */
const LABEL_ROOM = 34;

/**
 * The name of an icon-only button, shown while it is pointed at or focused
 * from the keyboard. Instant and styled, where the browser's own `title`
 * tooltip waits a second and looks like the operating system. Decorative: the
 * accessible name is the button's `aria-label`.
 *
 * Drawn on `document.body` at fixed coordinates rather than inside the
 * button: a card or list with `overflow: hidden` (or a scrolling one) used to
 * cut the label off, most visibly on the first row of a list. It sits above
 * the button, flips below when there is no room, and is aligned on the
 * button's right edge but kept inside the viewport.
 */
function useHoverLabel(label) {
  const anchor = useRef(null);
  const [position, setPosition] = useState(null);

  const show = useCallback(() => {
    const element = anchor.current;
    if (!label || !element) return;
    const rect = element.getBoundingClientRect();
    const above = rect.top >= LABEL_ROOM;
    setPosition({
      top: above ? rect.top - 6 : rect.bottom + 6,
      right: Math.max(8, window.innerWidth - rect.right),
      above,
    });
  }, [label]);
  const hide = useCallback(() => setPosition(null), []);

  // A label left behind by a scroll or a resize would point at nothing.
  useEffect(() => {
    if (!position) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [position, hide]);

  const handlers = {
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: (event) => {
      // Keyboard focus only: a click also focuses, and the label would linger.
      if (event.currentTarget.matches(':focus-visible')) show();
    },
    onBlur: hide,
  };

  const bubble =
    position &&
    createPortal(
      <span
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: position.top,
          right: position.right,
          transform: position.above ? 'translateY(-100%)' : undefined,
        }}
        className="bg-night pointer-events-none z-[1000] rounded-md px-2 py-1 text-[11px] leading-none font-medium whitespace-nowrap text-white shadow-md"
      >
        {label}
      </span>,
      document.body,
    );

  return { anchor, handlers, bubble };
}

/** Calls `ours` and then the caller's own handler of the same name. */
function chain(ours, theirs) {
  if (!theirs) return ours;
  return (event) => {
    ours(event);
    theirs(event);
  };
}

function withHandlers(handlers, props) {
  return Object.fromEntries(
    Object.entries(handlers).map(([name, handler]) => [name, chain(handler, props[name])]),
  );
}

export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'sm',
  isLoading = false,
  className,
  disabled,
  ...props
}) {
  const hover = useHoverLabel(label);
  return (
    <button
      type="button"
      aria-label={label}
      ref={hover.anchor}
      className={cn(
        BASE,
        iconVariant(variant),
        ICON_SIZES[size],
        'group/icon relative rounded-md p-0',
        className,
      )}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
      {...withHandlers(hover.handlers, props)}
    >
      {/* A pressed toggle shows its glyph filled: saved, starred, on. */}
      {isLoading ? (
        <Spinner />
      ) : (
        <Icon name={icon} size={17} strokeWidth={2} filled={props['aria-pressed'] === true} />
      )}
      {hover.bubble}
    </button>
  );
}

/** The same, but it navigates. */
export function IconLinkButton({
  to,
  icon,
  label,
  // A fuller name for screen readers ("Open Anirban's application") when the
  // short hover label ("View") only makes sense next to the row it is on.
  description,
  variant = 'ghost',
  size = 'sm',
  className,
}) {
  const hover = useHoverLabel(label);
  return (
    <Link
      to={to}
      ref={hover.anchor}
      {...hover.handlers}
      aria-label={description ?? label}
      className={cn(
        BASE,
        iconVariant(variant),
        ICON_SIZES[size],
        'group/icon relative rounded-md p-0',
        className,
      )}
    >
      <Icon name={icon} size={17} strokeWidth={2} />
      {hover.bubble}
    </Link>
  );
}

function Spinner() {
  return (
    <span
      className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden="true"
    />
  );
}
